export const HOMEWORK_STREAK_GOAL_DAYS = 20;

interface HomeworkStreakProps {
  value: number;
  goalDays?: number;
  editable?: boolean;
  saving?: boolean;
  status?: string | null;
  error?: string | null;
  onAdjust?: (change: -1 | 1) => void;
}

const HomeworkStreak: React.FC<HomeworkStreakProps> = ({
  value,
  goalDays = HOMEWORK_STREAK_GOAL_DAYS,
  editable = false,
  saving = false,
  status,
  error,
  onAdjust,
}) => {
  const displayedValue = Math.min(Math.max(value, 0), goalDays);
  const progress = goalDays > 0 ? (displayedValue / goalDays) * 100 : 0;

  return (
    <section aria-label="Homework streak" className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium text-gray-800">Homework Streak</h3>
        <span className="text-sm font-semibold text-gray-900">{displayedValue} {displayedValue === 1 ? 'day' : 'days'}</span>
      </div>
      <div
        role="progressbar"
        aria-label={`${displayedValue} of ${goalDays} homework streak days`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        className="h-2.5 overflow-hidden rounded-full bg-gray-200"
      >
        <div className="h-full rounded-full bg-mainBlue transition-[width]" style={{ width: `${progress}%` }} />
      </div>
      <p className="text-xs text-gray-500">{displayedValue} of {goalDays} day goal</p>
      {editable && (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            aria-label="Decrease homework streak by one day"
            disabled={saving || value === 0}
            onClick={() => onAdjust?.(-1)}
            className="min-h-[40px] min-w-[44px] rounded border border-gray-300 bg-white text-lg text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            &minus;
          </button>
          <button
            type="button"
            aria-label="Increase homework streak by one day"
            disabled={saving || value >= goalDays}
            onClick={() => onAdjust?.(1)}
            className="min-h-[40px] min-w-[44px] rounded border border-gray-300 bg-white text-lg text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            +
          </button>
          {saving && <span role="status" className="text-sm text-gray-500">Saving streak…</span>}
        </div>
      )}
      {status && <p role="status" className="text-sm text-green-700">{status}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </section>
  );
};

export default HomeworkStreak;